import { Autocomplete, Box, Checkbox, Chip, MenuItem, TextField, Typography } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import type { Company, Group, Role } from '@esg/esg-global-types';

import { company_label } from '../../../lib/app/company';
import { group_label } from '../../../lib/app/group';
import {
  countSelection,
  describeSelection,
  toggleCompanySelection,
  withSelectedGroups,
  type AccessSelection
} from './access_selection';

interface GroupCompanyAccessPickerProps {
  /** Groups the signed-in admin may manage — already scoped by the caller. */
  groups: Group[];
  roles: Role[];
  value: AccessSelection;
  onChange: (next: AccessSelection) => void;
  role: string;
  onRoleChange: (role: string) => void;
  /** Helper copy shown above the group picker. */
  description?: string;
}

/**
 * Group + company access picker shared by the Add user and Bulk import flows.
 *
 * Groups are chosen from a searchable multi-select, so the control stays usable
 * with hundreds of groups. Each chosen group then contributes a row of company
 * toggle chips, letting companies be selected across several groups in one pass.
 * Removing a group from the picker discards its company selections. A single
 * role applies to every selected company; per-company role overrides remain an
 * Edit user → Access concern.
 *
 * The shared `*Select.tsx` components were not reused here: each wraps a single
 * entity and syncs props into local state, which would fight the controlled,
 * effect-free selection this component needs.
 *
 * @param {Group[]} groups Groups the admin may manage.
 * @param {Role[]} roles Roles assignable to the selected companies.
 * @param {AccessSelection} value Current selection.
 * @param {(next: AccessSelection) => void} onChange Handler for selection changes.
 * @param {string} role Currently chosen role.
 * @param {(role: string) => void} onRoleChange Handler for role changes.
 * @param {string | undefined} description Helper copy shown above the group picker.
 * @returns {JSX.Element}
 */
const GroupCompanyAccessPicker = ({
  groups,
  roles,
  value,
  onChange,
  role,
  onRoleChange,
  description
}: GroupCompanyAccessPickerProps) => {
  // Follows the source list's order rather than click order, so the company
  // sections below don't reshuffle as groups are added and removed.
  // `Object.hasOwn`, not `in`: the latter also matches Object.prototype keys, so
  // a group literally named `constructor` would render as selected against an
  // empty selection.
  const selectedGroups = groups.filter((groupOption) => Object.hasOwn(value, groupOption.id));

  // Summary and payload must agree with what is on screen. A key in `value`
  // with no corresponding group renders no row — counting it would report
  // access the admin can neither see nor remove.
  const visibleSelection = withSelectedGroups(
    value,
    selectedGroups.map((groupOption) => groupOption.id)
  );
  const totals = countSelection(visibleSelection);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {description && (
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      )}

      <Autocomplete
        multiple
        disableCloseOnSelect
        options={groups}
        value={selectedGroups}
        onChange={(_event, nextGroups: Group[]) =>
          onChange(
            withSelectedGroups(
              value,
              nextGroups.map((groupOption) => groupOption.id)
            )
          )
        }
        getOptionLabel={(groupOption: Group) => groupOption.name}
        isOptionEqualToValue={(a: Group, b: Group) => a.id === b.id}
        limitTags={4}
        noOptionsText={
          groups.length === 0
            ? `No ${group_label.many.toLowerCase()} available to assign`
            : `No matching ${group_label.many.toLowerCase()}`
        }
        renderOption={({ key, ...liProps }, groupOption: Group, { selected }) => {
          const companyCount = (groupOption.companies ?? []).length;
          return (
            <li key={key} {...liProps}>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                checked={selected}
                // The row itself is the option; the checkbox is decoration, so
                // it must not be a second tab stop inside role="option".
                tabIndex={-1}
                disableRipple
                sx={{ mr: 1 }}
              />
              <Box>
                <Typography variant="body2">{groupOption.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {companyCount}{' '}
                  {(companyCount === 1 ? company_label.one : company_label.many).toLowerCase()}
                </Typography>
              </Box>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            required
            label={group_label.many}
            placeholder={
              selectedGroups.length === 0 ? `Search ${group_label.many.toLowerCase()}…` : ''
            }
          />
        )}
      />

      {selectedGroups.length === 0 ? (
        <Typography variant="caption" color="text.disabled">
          Choose one or more {group_label.many.toLowerCase()} to pick{' '}
          {company_label.many.toLowerCase()} from.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {selectedGroups.map((groupOption: Group) => {
            const selectedIds = visibleSelection[groupOption.id]?.companyIds ?? [];
            const companies = groupOption.companies ?? [];
            const headingId = `access-group-${groupOption.id}`;
            return (
              <Box key={groupOption.id}>
                <Typography id={headingId} variant="body2" color="text.secondary" gutterBottom>
                  {groupOption.name}
                  {selectedIds.length > 0 && ` — ${selectedIds.length} selected`}
                </Typography>
                <Box
                  // Ties the chips to their owning group; without it a screen
                  // reader reads one flat run of company buttons across every
                  // selected group, losing the distinction this feature adds.
                  role="group"
                  aria-labelledby={headingId}
                  sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}
                >
                  {companies.map((companyOption: Company) => {
                    const selected = selectedIds.includes(companyOption.id);
                    return (
                      <Chip
                        key={companyOption.id}
                        label={companyOption.name}
                        // Selection is otherwise signalled by colour and variant
                        // alone (WCAG 1.4.1, 4.1.2). MUI turns a Chip with
                        // onClick into a ButtonBase, so the role is already
                        // button — this supplies the missing state.
                        aria-pressed={selected}
                        onClick={() =>
                          onChange(toggleCompanySelection(value, groupOption.id, companyOption.id))
                        }
                        color={selected ? 'primary' : 'default'}
                        variant={selected ? 'filled' : 'outlined'}
                        sx={{ cursor: 'pointer' }}
                      />
                    );
                  })}
                  {companies.length === 0 && (
                    <Typography variant="caption" color="text.disabled">
                      No {company_label.many.toLowerCase()} available in this{' '}
                      {group_label.one.toLowerCase()}.
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <Typography
        // Announced on change, so a screen-reader user hears the running total
        // as they toggle chips rather than having to hunt for it.
        role="status"
        variant="caption"
        color={totals.companies > 0 ? 'text.secondary' : 'error'}
      >
        {totals.companies === 0
          ? `Select at least one ${company_label.one.toLowerCase()}.`
          : `${describeSelection(visibleSelection)} selected.`}
      </Typography>

      <TextField
        select
        label="Role"
        value={role}
        onChange={(event) => onRoleChange(event.target.value)}
        helperText="Applied to every selected company. Fine-tune later on Edit user → Access."
      >
        {roles
          .filter((roleOption: Role) => roleOption.name.toLowerCase() !== 'custom')
          .map((roleOption: Role) => (
            <MenuItem key={roleOption.id} value={roleOption.name.toLowerCase()}>
              {roleOption.name}
            </MenuItem>
          ))}
      </TextField>
    </Box>
  );
};

export default GroupCompanyAccessPicker;
